import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  ComponentProps,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { useDebouncedValueSafe } from 'src/hooks/useDebouncedValueSafe';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BuyerWithSalesDto,
  RetailClient,
  SellerBuyersOrderBy,
  SortDirection,
} from 'src/WebApiController';

import { BuyerCard } from './BuyerCard';

const BuyerSearchSize = 15;
const BuyerSearchPageNumber = 1;

export type UseRetailBuyerSelectorOptions = {
  disabled?: boolean;
  additionalOptions?: Record<string, string>;
  additionalOptionsPosition?: 'top' | 'bottom';
  placeholderText?: string | ContentId;
};

type BuyerSelectProps = Pick<
  ComponentProps<typeof PosSelect>,
  | 'placeholderText'
  | 'valueOptionsContent'
  | 'valueOptionsContentOverride'
  | 'searchable'
  | 'onSearchInputChange'
  | 'rootProps'
>;

export const useRetailBuyerSelector = ({
  disabled,
  additionalOptions,
  additionalOptionsPosition = 'bottom',
  placeholderText,
}: UseRetailBuyerSelectorOptions): {
  query: UseQueryResult<BuyerWithSalesDto[] | null>;
  posSelectProps: BuyerSelectProps;
} => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebouncedValueSafe(searchText);

  const shouldQuery =
    isOpen && !disabled && activeAccountWebClientConfig.activeAccountId != null;

  const onOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) setSearchText('');
  }, []);

  const query = useQuery({
    queryKey: [
      'GetSellerBuyersPaginated',
      activeAccountWebClientConfig.activeAccountId,
      debouncedSearchText,
    ],
    queryFn: async () => {
      if (!shouldQuery) return null;
      const result = await tryInvokeApi(
        () =>
          new RetailClient(
            activeAccountWebClientConfig
          ).getSellerBuyersPaginated(
            BuyerSearchSize,
            BuyerSearchPageNumber,
            SellerBuyersOrderBy.BuyerName,
            SortDirection.Ascending,
            null,
            null,
            debouncedSearchText || null
          ),
        (error) => trackError('RetailClient.getSellerBuyersPaginated', error)
      );
      return result?.items ?? null;
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });

  const buyerOptions = useMemo(() => {
    const sorted = query.data?.toSorted((a, b) =>
      a.buyerEmail.localeCompare(b.buyerEmail)
    );

    const results = sorted?.reduce(
      (r, cur) => {
        r[cur.buyerEmail] =
          `${cur.buyerEmail}${cur.buyerName ? ` (${cur.buyerName})` : ''}`;
        return r;
      },
      {} as Record<string, string>
    );

    return {
      ...(additionalOptionsPosition === 'top' ? additionalOptions : {}),
      ...(results ?? {}),
      ...(additionalOptionsPosition === 'bottom' ? additionalOptions : {}),
    };
  }, [query.data, additionalOptions, additionalOptionsPosition]);

  const buyerOptionsOverride = useMemo(() => {
    return query.data?.reduce(
      (r, cur) => {
        r[cur.buyerEmail] = <BuyerCard buyer={cur} />;
        return r;
      },
      {} as Record<string, ReactNode>
    );
  }, [query.data]);

  return {
    query,
    posSelectProps: {
      placeholderText: placeholderText ?? ContentId.ChooseBuyer,
      valueOptionsContent: buyerOptions,
      valueOptionsContentOverride: buyerOptionsOverride,
      searchable: true,
      onSearchInputChange: setSearchText,
      rootProps: { onOpenChange },
    },
  };
};
